import { useRef, useState } from 'react'
import { Button, Dialog, Image, Portal, Text } from '@chakra-ui/react'
import { LuImage } from 'react-icons/lu'
import { useParams } from 'react-router'
import { showSuccess } from '@/lib/alert'
import { useUploadAgendaImage } from '@/hooks/agendas/useUploadAgendaImage'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'

type Props = {
  renderTrigger?: (onClick: () => void) => React.ReactNode
}

const ACCEPTED_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
]

const MAX_SIZE_MB = 4
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

const ChangeAgendaCoverButton = ({ renderTrigger }: Props = {}) => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [sizeError, setSizeError] = useState(false)
  const uploadMutation = useUploadAgendaImage(agendaId!)

  const triggerInput = () => {
    setSizeError(false)
    inputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_SIZE_BYTES) {
      setSizeError(true)
      return
    }

    setSizeError(false)
    setSelectedFile(file)
    setPreviewUrl(URL.createObjectURL(file))
  }

  const handleClose = () => {
    setSelectedFile(null)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    uploadMutation.reset()
  }

  const handleConfirm = () => {
    if (!selectedFile) return
    uploadMutation.mutate(selectedFile, {
      onSuccess: () => {
        showSuccess('Capa alterada com sucesso!')
        handleClose()
      },
    })
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        value=""
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={ACCEPTED_FORMATS.join(',')}
        data-cy="upload-cover"
        data-testid="upload-cover"
      />
      {renderTrigger ? (
        renderTrigger(triggerInput)
      ) : (
        <Button
          size={{ base: 'xs', sm: 'sm' }}
          colorPalette="blue"
          bgColor="blue.700"
          onClick={triggerInput}
        >
          <LuImage />
          Alterar capa
        </Button>
      )}

      {sizeError && (
        <Text fontSize="xs" color="red.500" mt={1}>
          A imagem deve ter no máximo {MAX_SIZE_MB}MB.
        </Text>
      )}

      {selectedFile && previewUrl && (
        <Dialog.Root
          size="sm"
          placement="center"
          open
          onOpenChange={({ open }) => { if (!open) handleClose() }}
        >
          <Portal>
            <CustomDialogBackdrop />
            <Dialog.Positioner>
              <Dialog.Content m={6}>
                <Dialog.Header>
                  <Dialog.Title>Alterar capa</Dialog.Title>
                </Dialog.Header>

                <Dialog.Body>
                  <Image
                    src={previewUrl}
                    alt="Prévia da capa"
                    w="full"
                    maxH="64"
                    objectFit="cover"
                    borderRadius="md"
                  />
                </Dialog.Body>

                <Dialog.Footer>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClose}
                    disabled={uploadMutation.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    colorPalette="primary"
                    loading={uploadMutation.isPending}
                    onClick={handleConfirm}
                  >
                    Confirmar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      )}
    </>
  )
}

export default ChangeAgendaCoverButton
